import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Tooltip,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import {
    Schedule as ScheduleIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PauseCircle as PauseIcon,
    PlayCircle as PlayIcon,
    Straighten as MeasurementsIcon,
    MonitorWeight as WeightIcon,
    CameraAlt as PhotoIcon,
    Description as FormIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import { useMessageContext } from "../../contexts/MessageContext";
import { useCompany } from "../../contexts/CompanyContext";
import { getErrorMessage } from "../../enums/ErrorMessages";
import {
    getScheduledCheckIns,
    getScheduledCheckInById,
    createScheduledCheckIn,
    updateScheduledCheckIn,
    deleteScheduledCheckIn,
    toggleScheduledCheckInActive
} from "../../services/ScheduledCheckInService";
import ScheduledCheckInDialog from "../check-ins/ScheduledCheckInDialog";
import { colors, borders, shadows } from "../ui/colors";
import { t as coachT } from "../dashboard/coach/coachTokens";
import { RTL } from "../../cache/CacheRtl";

const FREQUENCY_LABELS = {
    DAILY: "יומי",
    WEEKLY: "שבועי",
    MONTHLY: "חודשי"
};

const DAY_LABELS = {
    SUNDAY: "יום א'",
    MONDAY: "יום ב'",
    TUESDAY: "יום ג'",
    WEDNESDAY: "יום ד'",
    THURSDAY: "יום ה'",
    FRIDAY: "יום ו'",
    SATURDAY: "שבת"
};

const ITEM_TYPE_CONFIG = {
    BODY_MEASUREMENT: { icon: <MeasurementsIcon sx={{ fontSize: "16px" }} />, color: "#8B5CF6", label: "מדידות" },
    BODY_WEIGHT: { icon: <WeightIcon sx={{ fontSize: "16px" }} />, color: colors.info, label: "משקל" },
    PROGRESS_PHOTO: { icon: <PhotoIcon sx={{ fontSize: "16px" }} />, color: colors.success, label: "תמונות" },
    FORM: { icon: <FormIcon sx={{ fontSize: "16px" }} />, color: colors.warning, label: "טופס" }
};

const ITEM_TYPE_COACH = {
    BODY_MEASUREMENT: coachT.purple,
    BODY_WEIGHT: coachT.blue,
    PROGRESS_PHOTO: coachT.green,
    FORM: coachT.amber,
};

const formatDate = (epochSeconds) => {
    if (!epochSeconds) return "-";
    return new Date(epochSeconds * 1000).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatScheduleDescription = (schedule) => {
    if (schedule.frequency === "WEEKLY") {
        const dayLabels = (schedule.scheduleDays || []).map(d => DAY_LABELS[d] || d).join(", ");
        const interval = schedule.intervalWeeks && schedule.intervalWeeks > 1
            ? `כל ${schedule.intervalWeeks} שבועות`
            : "שבועי";
        return dayLabels ? `${interval} - ${dayLabels}` : interval;
    }
    if (schedule.frequency === "MONTHLY") {
        const days = (schedule.daysOfMonth || []).sort((a, b) => a - b).join(", ");
        const interval = schedule.intervalMonths && schedule.intervalMonths > 1
            ? `כל ${schedule.intervalMonths} חודשים`
            : "חודשי";
        return days ? `${interval} - יום ${days}` : interval;
    }
    return FREQUENCY_LABELS[schedule.frequency] || schedule.frequency;
};

const tdBase = { borderBottom: borders.default, py: 0, px: 2, height: "56px", fontSize: "13px", color: colors.textPrimary };
const thBase = { ...tdBase, height: "42px", color: colors.textMuted, fontSize: "11px", fontWeight: 600, backgroundColor: colors.bg, letterSpacing: "0.3px" };

const tdCoach = { borderBottom: `1px solid ${coachT.borderLight}`, py: 0, px: 2, height: "56px", fontSize: "13px", color: coachT.text };
const thCoach = { ...tdCoach, height: "42px", color: coachT.t3, fontSize: "10.5px", fontWeight: 700, backgroundColor: coachT.bg, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${coachT.border}` };

const ClientPageScheduledCheckIns = ({ client, coach = false }) => {
    const th = coach ? thCoach : thBase;
    const td = coach ? tdCoach : tdBase;
    const { notificationHandler } = useMessageContext();
    const { company } = useCompany();

    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchSchedules = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getScheduledCheckIns(0, 100, company?.id);
            const list = res.data?.content || res.data || [];
            // The list response only contains sendToAll/targetTraineeCount, not the
            // actual target trainees. Load each schedule's details to keep only the
            // ones that explicitly target this trainee.
            const detailed = await Promise.all(
                list
                    .filter(s => !s.sendToAll && (s.targetTraineeCount || 0) > 0)
                    .map(s => getScheduledCheckInById(s.id).then(r => r.data).catch(() => null))
            );
            const forTrainee = detailed.filter(
                s => s && (s.targetTrainees || []).some(t => t.id === client.id)
            );
            setSchedules(forTrainee);
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה בטעינת הצ'ק-אינים המתוזמנים");
            setSchedules([]);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id, company?.id]);

    useEffect(() => {
        fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id, company?.id, reload]);

    const handleReload = () => setReload(prev => !prev);

    const handleCreate = () => {
        setEditData(null);
        setDialogOpen(true);
    };

    const handleEdit = async (schedule) => {
        try {
            const res = await getScheduledCheckInById(schedule.id);
            setEditData(res.data);
            setDialogOpen(true);
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה בטעינת הנתונים");
        }
    };

    const handleSubmit = async (data, editId) => {
        if (editId) {
            await updateScheduledCheckIn(editId, data);
            notificationHandler.success("צ'ק-אין מתוזמן עודכן בהצלחה");
        } else {
            await createScheduledCheckIn(data);
            notificationHandler.success("צ'ק-אין מתוזמן נוצר בהצלחה");
        }
        handleReload();
    };

    const handleToggleActive = async (schedule) => {
        try {
            await toggleScheduledCheckInActive(schedule.id);
            notificationHandler.success(schedule.isActive ? "צ'ק-אין מתוזמן הושהה" : "צ'ק-אין מתוזמן הופעל");
            handleReload();
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה בעדכון הסטטוס");
        }
    };

    // Rebuild a full update payload from an existing (detailed) schedule, used
    // to unassign this trainee from a schedule that is shared with others.
    const buildUpdatePayload = (schedule, traineeIds) => ({
        name: schedule.name || null,
        frequency: schedule.frequency,
        scheduleDays: schedule.frequency === "WEEKLY" ? (schedule.scheduleDays || []) : [],
        daysOfMonth: schedule.frequency === "MONTHLY" ? (schedule.daysOfMonth || []) : [],
        intervalWeeks: schedule.frequency === "WEEKLY" ? (schedule.intervalWeeks || 1) : null,
        intervalMonths: schedule.frequency === "MONTHLY" ? (schedule.intervalMonths || 1) : null,
        scheduleTime: schedule.scheduleTime,
        sendToAll: false,
        traineeIds,
        items: (schedule.items || []).map(i => ({
            type: i.type,
            required: !!i.required,
            formTemplateId: i.type === "FORM" ? (i.formTemplateId || null) : null
        }))
    });

    const handleDeleteConfirm = async () => {
        if (deleteTarget) {
            try {
                const otherTrainees = (deleteTarget.targetTrainees || []).filter(t => t.id !== client.id);
                if (otherTrainees.length > 0) {
                    // Shared with other trainees — only remove the current one so
                    // the schedule stays active for everybody else.
                    await updateScheduledCheckIn(deleteTarget.id, buildUpdatePayload(deleteTarget, otherTrainees.map(t => t.id)));
                    notificationHandler.success("הצ'ק-אין המתוזמן הוסר מהמתאמן");
                } else {
                    await deleteScheduledCheckIn(deleteTarget.id);
                    notificationHandler.success("צ'ק-אין מתוזמן נמחק בהצלחה");
                }
                handleReload();
            } catch (e) {
                notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה במחיקה");
            }
        }
        setDeleteDialogOpen(false);
        setDeleteTarget(null);
    };

    const deleteIsShared = (deleteTarget?.targetTrainees || []).filter(t => t.id !== client.id).length > 0;

    return (
        <>
            <Box sx={coach ? {} : { margin: "20px" }}>
                <RTL>
                    <Paper
                        dir="rtl"
                        sx={{
                            backgroundColor: coach ? coachT.white : colors.white,
                            border: coach ? `1px solid ${coachT.border}` : borders.default,
                            borderRadius: coach ? coachT.r : borders.radius.lg,
                            boxShadow: coach ? coachT.sh : shadows.card,
                            overflow: "hidden",
                        }}
                        elevation={0}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: coach ? "16px 22px" : "16px 20px",
                                backgroundColor: coach ? coachT.bg : colors.bg,
                                borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
                            }}
                        >
                            <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
                                {coach ? (
                                    <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        <ScheduleIcon sx={{color: coachT.t2, fontSize: 22}}/>
                                    </Box>
                                ) : (
                                    <ScheduleIcon sx={{ color: colors.textSecondary, fontSize: "20px" }} />
                                )}
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: coach ? 800 : 600,
                                            fontSize: coach ? "17px" : "16px",
                                            color: coach ? coachT.text : colors.textPrimary,
                                            letterSpacing: coach ? "-0.2px" : 0,
                                        }}
                                    >
                                        צ'ק-אין מתוזמן{coach ? "" : ` - ${client?.name}`}
                                    </Typography>
                                    {!coach && (
                                        <Typography sx={{fontSize: "14px", color: colors.textSecondary, marginTop: "4px"}}>
                                            כל הצ'ק-אינים המתוזמנים של המתאמן
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {coach ? (
                                <Box component="button" onClick={handleCreate} sx={{
                                    display: "inline-flex", alignItems: "center", gap: "5px",
                                    background: coachT.green, color: "#fff",
                                    height: 34, px: "15px", borderRadius: coachT.rs,
                                    fontSize: "13px", fontWeight: 600,
                                    border: "none", cursor: "pointer", fontFamily: "inherit",
                                    transition: "all .14s",
                                    "&:hover": {background: coachT.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                                }}>
                                    <AddIcon sx={{fontSize: 16}}/>
                                    שליחת צ'ק-אין מתוזמן
                                </Box>
                            ) : (
                                <Button
                                    onClick={handleCreate}
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    sx={{
                                        backgroundColor: colors.primary,
                                        color: colors.white,
                                        fontSize: "13px",
                                        padding: "8px 16px",
                                        "&:hover": { backgroundColor: colors.primaryDark }
                                    }}
                                    size="small"
                                >
                                    שליחת צ'ק-אין מתוזמן
                                </Button>
                            )}
                        </Box>

                        {/* Content */}
                        <Box sx={{ padding: coach ? "16px 22px" : "20px" }}>
                            {loading ? (
                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    minHeight: "200px"
                                }}>
                                    <CircularProgress sx={{ color: coach ? coachT.green : colors.primary }} />
                                </Box>
                            ) : schedules.length > 0 ? (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {["שם", "תדירות", "פריטים", "שליחה הבאה", "סטטוס"].map((header) => (
                                                    <TableCell key={header} sx={th}>
                                                        {header}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={th} style={{ textAlign: "left" }}>פעולות</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {schedules.map((schedule) => (
                                                <TableRow
                                                    key={schedule.id}
                                                    sx={{
                                                        "&:hover": { backgroundColor: coach ? coachT.greenLight : colors.bg },
                                                        transition: "background-color 0.2s",
                                                        opacity: schedule.isActive ? 1 : 0.6
                                                    }}
                                                >
                                                    <TableCell sx={td}>
                                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: coach ? coachT.text : colors.textPrimary }}>
                                                            {schedule.name || "צ'ק-אין מתוזמן"}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "12px", color: coach ? coachT.t3 : colors.textMuted }}>
                                                            {schedule.scheduleTime}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ ...td, color: coach ? coachT.t2 : colors.textSecondary }}>
                                                        {formatScheduleDescription(schedule)}
                                                    </TableCell>
                                                    <TableCell sx={td}>
                                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                                            {(schedule.items || []).map((item, idx) => {
                                                                const config = ITEM_TYPE_CONFIG[item.type];
                                                                if (!config) return null;
                                                                const iconColor = coach ? (ITEM_TYPE_COACH[item.type] || coachT.t2) : config.color;
                                                                return (
                                                                    <Tooltip key={idx} title={config.label}>
                                                                        <Box sx={{
                                                                            width: 28,
                                                                            height: 28,
                                                                            borderRadius: coach ? coachT.rxs : borders.radius.sm,
                                                                            backgroundColor: `${iconColor}15`,
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            "& svg": { color: iconColor }
                                                                        }}>
                                                                            {config.icon}
                                                                        </Box>
                                                                    </Tooltip>
                                                                );
                                                            })}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ ...td, color: coach ? coachT.t2 : colors.textSecondary }}>
                                                        {formatDate(schedule.nextExecutionDate)}
                                                    </TableCell>
                                                    <TableCell sx={td}>
                                                        <Chip
                                                            label={schedule.isActive ? "פעיל" : "מושהה"}
                                                            size="small"
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: "11px",
                                                                ...(schedule.isActive
                                                                    ? { backgroundColor: coach ? coachT.greenLight : colors.successBg, color: coach ? coachT.greenText : colors.success }
                                                                    : { backgroundColor: coach ? coachT.amberLight : colors.warningBg, color: coach ? coachT.amberText : colors.warning }),
                                                                "& .MuiChip-label": { padding: "0 8px" }
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={td}>
                                                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                                            <Tooltip title={schedule.isActive ? "השהה" : "הפעל"}>
                                                                <IconButton
                                                                    onClick={() => handleToggleActive(schedule)}
                                                                    size="small"
                                                                    sx={{ color: schedule.isActive ? (coach ? coachT.amber : colors.warning) : (coach ? coachT.green : colors.success) }}
                                                                >
                                                                    {schedule.isActive ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="עריכה">
                                                                <IconButton
                                                                    onClick={() => handleEdit(schedule)}
                                                                    size="small"
                                                                    sx={{
                                                                        color: coach ? coachT.t2 : colors.primary,
                                                                        "&:hover": { backgroundColor: coach ? coachT.greenLight : colors.primaryLight, color: coach ? coachT.green : undefined }
                                                                    }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={(schedule.targetTrainees || []).filter(t => t.id !== client.id).length > 0 ? "הסרה מהמתאמן" : "מחיקה"}>
                                                                <IconButton
                                                                    onClick={() => { setDeleteTarget(schedule); setDeleteDialogOpen(true); }}
                                                                    size="small"
                                                                    sx={{
                                                                        color: coach ? coachT.t2 : colors.error,
                                                                        "&:hover": { backgroundColor: coach ? coachT.redLight : colors.errorBg, color: coach ? coachT.red : undefined }
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: "200px",
                                    flexDirection: "column",
                                    gap: 2
                                }}>
                                    <ScheduleIcon sx={{ fontSize: "48px", color: coach ? coachT.border : colors.borderHover }} />
                                    <Typography sx={{
                                        fontSize: "16px",
                                        fontWeight: 500,
                                        color: coach ? coachT.t3 : colors.textSecondary,
                                        textAlign: "center"
                                    }}>
                                        אין צ'ק-אינים מתוזמנים
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: "14px",
                                        color: coach ? coachT.t3 : colors.textMuted,
                                        textAlign: "center"
                                    }}>
                                        לחץ על "שליחת צ'ק-אין מתוזמן" כדי ליצור צ'ק-אין מתוזמן ראשון
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </RTL>
            </Box>

            {/* Create / Edit Dialog */}
            <ScheduledCheckInDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditData(null); }}
                onSubmit={handleSubmit}
                editData={editData}
                preSelectedTrainee={{ id: client.id, name: client.name }}
                coach={coach}
            />

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: coach ? coachT.r : "12px", direction: "rtl" } }}
            >
                <DialogTitle sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 24px",
                    backgroundColor: coach ? coachT.bg : undefined,
                    borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
                }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: coach ? 800 : 600, color: coach ? coachT.text : colors.textPrimary }}>
                        {deleteIsShared ? "הסרת צ'ק-אין מתוזמן מהמתאמן" : "מחיקת צ'ק-אין מתוזמן"}
                    </Typography>
                    <IconButton
                        onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
                        size="small"
                        sx={{ color: coach ? coachT.t2 : colors.textSecondary }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: "24px" }}>
                    <Typography sx={{ fontSize: "14px", color: coach ? coachT.t2 : colors.textSecondary }}>
                        {deleteIsShared
                            ? `הצ'ק-אין המתוזמן "${deleteTarget?.name || "צ'ק-אין מתוזמן"}" משויך גם למתאמנים נוספים. הסרה כאן תסיר אותו מ${client?.name} בלבד, והוא יישאר פעיל עבור שאר המתאמנים. להמשיך?`
                            : `האם אתה בטוח שברצונך למחוק את הצ'ק-אין המתוזמן "${deleteTarget?.name || "צ'ק-אין מתוזמן"}"?`}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: "16px 24px", borderTop: coach ? `1px solid ${coachT.border}` : borders.default, gap: 2 }}>
                    <Button
                        onClick={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            borderColor: coach ? coachT.border : colors.border,
                            color: coach ? coachT.t2 : colors.textSecondary,
                            borderRadius: coach ? coachT.rs : undefined,
                            "&:hover": { borderColor: coach ? coachT.t3 : colors.borderHover, backgroundColor: coach ? coachT.bg : colors.bg }
                        }}
                    >
                        ביטול
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        sx={{
                            flex: 1,
                            backgroundColor: coach ? coachT.red : colors.errorText,
                            borderRadius: coach ? coachT.rs : undefined,
                            boxShadow: coach ? "none" : undefined,
                            "&:hover": { backgroundColor: coach ? coachT.redText : "#B91C1C", boxShadow: coach ? "none" : undefined }
                        }}
                    >
                        {deleteIsShared ? "הסר" : "מחק"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ClientPageScheduledCheckIns;
